import {
  ArrowDropDown,
  CloseOutlined,
  EventNote,
  FilterAltOutlined,
  LabelOutlined,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Paper,
  Popover,
  Typography,
  useTheme,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Grid2,
} from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as dayjs from "dayjs";
import { useState } from "react";

type FilterItemProps = {
  title: string;
  value: any;
  setValue: (value: any) => void;
};

const FilterItem = ({ title, value, setValue }: FilterItemProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const theme = useTheme();
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (event: React.SyntheticEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const styles = {
    text: {
      fontSize: "14px",
      fontWeight: 500,
      height: "20px",
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    paperBrick: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      maxWidth: "100%",
      gap: 0.5,
      flexShrink: 1,
      "&:hover": {
        cursor: "pointer",
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
      },
    },
    size: { fontSize: 20 },
    popover: {
      "& .MuiPopover-paper": {
        // width: "300px",
        // height: "300px",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 4px 14px 0px rgba(0, 0, 0, 0.5)"
            : "0px 4px 14px 0px rgba(16, 24, 40, 0.12)",
      },
    },
  };

  return (
    <>
      <Paper
        variant="brick"
        sx={styles.paperBrick}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        <EventNote sx={styles.size} />
        <Typography sx={styles.text} noWrap>
          {title}
        </Typography>
        {!!value && (
          <Typography sx={styles.text}>
            {": " + dayjs(value).toDate().toLocaleDateString()}
          </Typography>
        )}
        <ArrowDropDown sx={styles.size} />
      </Paper>
      <Popover
        id={"modal-data"}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={styles.popover}
      >
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={"pt-br"}
        >
          <DateCalendar
            value={value}
            onChange={(newValue) => setValue(newValue)}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
};

const TypeFilterItem = ({
  types,
  selectedTypes,
  setSelectedTypes,
}: {
  types: string[];
  selectedTypes: string[];
  setSelectedTypes: (value: string[]) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const theme = useTheme();

  const handleClick = (event: React.SyntheticEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleToggleType = (type: string) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter((t) => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  const styles = {
    text: {
      fontSize: "14px",
      fontWeight: 500,
      height: "20px",
      display: "block",
    },
    paperBrick: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      paddingLeft: 1.5,
      gap: 0.5,
      flexShrink: 1,
      "&:hover": {
        cursor: "pointer",
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
      },
    },
    size: { fontSize: 20 },
    popover: {
      "& .MuiPopover-paper": {
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 4px 14px 0px rgba(0, 0, 0, 0.5)"
            : "0px 4px 14px 0px rgba(16, 24, 40, 0.12)",
        padding: 2,
      },
    },
    selectedType: {
      fontSize: "14px",
      fontWeight: 500,
      marginLeft: "4px",
    },
    moreTypes: {
      fontSize: "14px",
      fontWeight: 500,
      marginLeft: "4px",
      color: theme.palette.text.secondary,
    },
  };

  // Only show the first selected type, and a "+N" if more are selected
  const firstType = selectedTypes[0];
  const moreCount = selectedTypes.length > 1 ? selectedTypes.length - 1 : 0;

  return (
    <>
      <Paper
        variant="brick"
        sx={styles.paperBrick}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        <LabelOutlined sx={styles.size} />
        <Typography sx={styles.text} noWrap>
          {"Tipo:"}
        </Typography>
        {!!selectedTypes.length && (
          <>
            <Typography sx={styles.selectedType}>{` ${firstType}`}</Typography>
            {moreCount > 0 && (
              <Tooltip title={selectedTypes.slice(1).join(", ")}>
                <Typography sx={styles.moreTypes}>{`+${moreCount}`}</Typography>
              </Tooltip>
            )}
          </>
        )}
        <ArrowDropDown sx={styles.size} />
      </Paper>
      <Popover
        id={"modal-type"}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={styles.popover}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {types.map((type) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  checked={selectedTypes.includes(type)}
                  onChange={() => handleToggleType(type)}
                />
              }
              label={type}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

interface ReportFiltersProps {
  types: string[];
  selectedTypes: string[];
  setSelectedTypes: (value: string[]) => void;
  deletedAt: dayjs.Dayjs | null;
  setDeletedAt: (value: dayjs.Dayjs | null) => void;
}

export default function LixeiraFilter({
  types,
  selectedTypes,
  setSelectedTypes,
  deletedAt,
  setDeletedAt,
}: ReportFiltersProps) {
  const theme = useTheme();

  const styles = {
    backgroundBox: {
      display: "flex",
      width: { xs: "100%", md: "100%" },
      flexDirection: { xs: "column", sm: "row" },
      backgroundColor: theme.palette.background.textField,
      borderRadius: "5px",
      gap: 1.5,
      padding: "4px 4px 4px 8px",
      justifyContent: "start",
      alignItems: { xs: "start", sm: "center" },
    },
    boxTitle: {
      display: "flex",
      flexDirection: "row",

      gap: 0.5,
    },
    text: {
      // color: theme.palette.text.secondary,
      fontSize: "14px",
      fontWeight: 500,
      height: "20px",
    },
    paperBrick: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      paddingLeft: 1.5,
      //width: "fit-content",
      gap: 0.5,
      "&:hover": {
        cursor: "pointer",
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
      },
    },
    size: { fontSize: 20 },
  };
  const handleClearFilters = () => {
    setSelectedTypes([]);
    setDeletedAt(null);
  };
  const hasFilters = selectedTypes.length > 0 || deletedAt !== null;

  return (
    <Box
      sx={{
        display: "flex",
        width: "fit-content",
        justifyContent: "space-between",
      }}
    >
      <Box sx={styles.backgroundBox}>
        <Grid2
          container
          rowSpacing={1}
          spacing={1}
          sx={{ alignItems: "center" }}
        >
          <Grid2 size={"auto"}>
            <Box sx={styles.boxTitle}>
              <FilterAltOutlined sx={styles.size} />
              <Typography sx={styles.text}>{"Filtros:"}</Typography>
            </Box>
          </Grid2>
          <Grid2 size={"auto"}>
            <TypeFilterItem
              types={types}
              selectedTypes={selectedTypes}
              setSelectedTypes={setSelectedTypes}
            />
          </Grid2>
          <Grid2 size={"auto"}>
            <FilterItem
              title="Data de Exclusão"
              value={deletedAt}
              setValue={setDeletedAt}
            />
          </Grid2>
          <Grid2 size={"auto"}>
            {hasFilters && (
              <Button
                variant="text"
                onClick={handleClearFilters}
                sx={{
                  height: "32px",
                  color: theme.palette.text.primary,
                }}
                startIcon={<CloseOutlined />}
              >
                {"Limpar Filtros"}
              </Button>
            )}
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
}
